/**
 * Writing BibTeX and biblatex entries by hand-rolled rules rather than a template.
 *
 * Four jobs, each small and each easy to get subtly wrong:
 *
 * 1. **Escaping.** `& % $ # _ { } ~ ^ \ < >` mean something to LaTeX; a title that
 *    contains one either fails to compile or prints the wrong glyph. They become `\&`,
 *    `\%`, `\textasciitilde{}` and so on.
 * 2. **ASCII-safe output, on request.** Classic 8-bit BibTeX sorts and abbreviates
 *    `é` correctly only when it is written `{\'e}`. biber and modern LaTeX read UTF-8,
 *    so this is an option rather than the default.
 * 3. **Names.** BibTeX reads "Family, Given" unambiguously and splits "Given Family"
 *    on its last word. An organisation has to be double-braced — `{{World Health
 *    Organization}}` — or BibTeX files it under "Organization, World Health".
 * 4. **Keys.** First author's surname + year + first significant title word, ASCII
 *    and lowercase (`santos2022robokrill`) — the convention CitationEasy's other BibTeX
 *    tools use. Colliding keys get a, b, c suffixes.
 */

import {
  type PersonName,
  parsePersonName,
} from "@/lib/tool-kit/citation-parts";

/** One `name = {value}` line. `bare` values (month macros) are written unbraced. */
export interface BibField {
  name: string;
  value: string;
  bare?: boolean;
}

export interface BibTextOptions {
  /** Brace every capitalised word after the first so styles keep its case. */
  protectCapitals: boolean;
  /** Write accented letters and typographic punctuation as LaTeX commands. */
  asciiSafe: boolean;
}

/* ---------------------------------------------------------------- escaping */

const LATEX_SPECIALS: Record<string, string> = {
  "\\": "\\textbackslash{}",
  "{": "\\{",
  "}": "\\}",
  "&": "\\&",
  "%": "\\%",
  $: "\\$",
  "#": "\\#",
  _: "\\_",
  "~": "\\textasciitilde{}",
  "^": "\\textasciicircum{}",
  "<": "\\textless{}",
  ">": "\\textgreater{}",
};

const RE_LATEX_SPECIAL = /[\\{}&%$#_~^<>]/g;
// A real tag only — `<i>`, `</sub>`, `<mml:math …>` — so "aged <65 and >80" survives.
const RE_HTML_TAG = /<\/?[a-z][\w:-]*(?:\s[^<>]*)?\/?>/gi;
const RE_WHITESPACE_RUN = /\s+/g;
const RE_ENTITY = /&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos|nbsp);/gi;

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

function decodeEntity(_match: string, body: string): string {
  const lower = body.toLowerCase();
  if (lower.startsWith("#x")) {
    return String.fromCodePoint(Number.parseInt(lower.slice(2), 16));
  }
  if (lower.startsWith("#")) {
    return String.fromCodePoint(Number.parseInt(lower.slice(1), 10));
  }
  return NAMED_ENTITIES[lower] ?? _match;
}

/**
 * Metadata as plain text: tags stripped (Crossref titles carry `<i>`, `<sub>`,
 * MathML), the common entities decoded, whitespace collapsed.
 */
export function plainText(value: string): string {
  return value
    .replace(RE_HTML_TAG, "")
    .replace(RE_ENTITY, decodeEntity)
    .replace(RE_WHITESPACE_RUN, " ")
    .trim();
}

export function escapeLatex(text: string): string {
  return text.replace(RE_LATEX_SPECIAL, (char) => LATEX_SPECIALS[char] ?? char);
}

/* ------------------------------------------------------------ ASCII-safe */

/** Letters with no decomposition, written as their LaTeX commands. */
const SPECIAL_LETTERS: Record<string, string> = {
  ß: "{\\ss}",
  æ: "{\\ae}",
  Æ: "{\\AE}",
  œ: "{\\oe}",
  Œ: "{\\OE}",
  ø: "{\\o}",
  Ø: "{\\O}",
  å: "{\\aa}",
  Å: "{\\AA}",
  ł: "{\\l}",
  Ł: "{\\L}",
  ı: "{\\i}",
  "\u2013": "--",
  "\u2014": "---",
  "\u2018": "`",
  "\u2019": "'",
  "\u201C": "``",
  "\u201D": "''",
  "\u00A0": "~",
};

/** Combining mark → accent command. Symbol accents take no braces: `\'e`. */
const ACCENTS: Record<string, { command: string; symbol: boolean }> = {
  "\u0300": { command: "\\`", symbol: true },
  "\u0301": { command: "\\'", symbol: true },
  "\u0302": { command: "\\^", symbol: true },
  "\u0303": { command: "\\~", symbol: true },
  "\u0304": { command: "\\=", symbol: true },
  "\u0306": { command: "\\u", symbol: false },
  "\u0307": { command: "\\.", symbol: true },
  "\u0308": { command: '\\"', symbol: true },
  "\u030A": { command: "\\r", symbol: false },
  "\u030B": { command: "\\H", symbol: false },
  "\u030C": { command: "\\v", symbol: false },
  "\u0323": { command: "\\d", symbol: false },
  "\u0327": { command: "\\c", symbol: false },
  "\u0328": { command: "\\k", symbol: false },
};

const RE_COMBINING_MARK = /[\u0300-\u036f]/;
// Latin base letters only: Greek and Cyrillic stay UTF-8 instead of gaining accent commands.
const RE_LETTER_WITH_MARKS = /([A-Za-z])([\u0300-\u036f]+)/g;
const RE_SPECIAL_LETTER =
  /[ßæÆœŒøØåÅłŁı\u2013\u2014\u2018\u2019\u201C\u201D\u00A0]/g;

function accentCommand(base: string, marks: string): string | null {
  // i and j lose their dot under an accent: {\'\i}, not {\'i}.
  const dotless = base === "i" || base === "j";
  let inner = dotless ? `\\${base}` : base;
  const list = [...marks];
  for (const mark of list) {
    const accent = ACCENTS[mark];
    if (!accent) {
      return null;
    }
    const plainBase = inner.length === 1 || dotless;
    const single = list.length === 1 && accent.symbol && plainBase;
    inner = single
      ? `${accent.command}${inner}`
      : `${accent.command}{${inner}}`;
  }
  return `{${inner}}`;
}

/**
 * {\'e}, {\c{c}}, {\ss}, -- and `` for classic BibTeX. Letters this cannot express —
 * Greek, Cyrillic, CJK — are left as UTF-8 rather than dropped.
 */
export function toAsciiLatex(text: string): string {
  const special = text.replace(
    RE_SPECIAL_LETTER,
    (char) => SPECIAL_LETTERS[char] ?? char
  );
  if (!RE_COMBINING_MARK.test(special.normalize("NFD"))) {
    return special;
  }
  return special
    .normalize("NFD")
    .replace(
      RE_LETTER_WITH_MARKS,
      (match, base: string, marks: string) =>
        accentCommand(base, marks) ?? match.normalize("NFC")
    )
    .normalize("NFC");
}

/* ------------------------------------------------------------- field text */

const RE_HAS_UPPER = /\p{Lu}/u;

/** Brace each word after the first that contains a capital: {DNA}, {RoboKrill}. */
export function protectCapitals(text: string): string {
  return text
    .split(" ")
    .map((word, index) => {
      if (index === 0 || !RE_HAS_UPPER.test(word)) {
        return word;
      }
      return `{${word}}`;
    })
    .join(" ");
}

/** Plain metadata → a BibTeX field value: escaped, optionally protected and ASCII. */
export function bibText(
  value: string,
  options: BibTextOptions,
  { isTitle = false }: { isTitle?: boolean } = {}
): string {
  let text = escapeLatex(plainText(value));
  if (isTitle && options.protectCapitals) {
    text = protectCapitals(text);
  }
  return options.asciiSafe ? toAsciiLatex(text) : text;
}

/* ------------------------------------------------------------------ names */

const ORGANIZATION_WORDS = new Set([
  "academy",
  "administration",
  "agency",
  "alliance",
  "association",
  "authority",
  "board",
  "bureau",
  "center",
  "centre",
  "clinic",
  "collaboration",
  "collaborative",
  "college",
  "commission",
  "committee",
  "company",
  "consortium",
  "corporation",
  "council",
  "department",
  "federation",
  "foundation",
  "gmbh",
  "government",
  "group",
  "hospital",
  "inc",
  "initiative",
  "institute",
  "institution",
  "investigators",
  "laboratories",
  "laboratory",
  "league",
  "library",
  "limited",
  "llc",
  "ltd",
  "ministry",
  "museum",
  "network",
  "office",
  "organisation",
  "organization",
  "panel",
  "partnership",
  "press",
  "program",
  "programme",
  "project",
  "publishers",
  "publishing",
  "school",
  "service",
  "services",
  "society",
  "staff",
  "team",
  "trust",
  "union",
  "university",
  "writers",
]);

const RE_NAME_TOKEN_SPLIT = /[\s,.;:()]+/;
const RE_DIGIT = /\d/;
const RE_OF_BETWEEN_WORDS = /\s(?:of|for|and|&)\s/i;

/**
 * A heuristic, because the lookup services hand back a group author as a plain string:
 * a digit, a joining word ("of", "for", "and") or an institutional noun marks an
 * organisation. "COVID-19 Investigators", "World Health Organization" and "Royal
 * Society of Chemistry" match; "Sara Oliveira Santos" does not.
 */
export function looksLikeOrganization(name: string): boolean {
  if (RE_DIGIT.test(name) || RE_OF_BETWEEN_WORDS.test(name)) {
    return true;
  }
  return name
    .toLowerCase()
    .split(RE_NAME_TOKEN_SPLIT)
    .some((token) => ORGANIZATION_WORDS.has(token));
}

/** A person or group author, read from a natural-order string ("Mary Jane Smith"). */
export function personFromNatural(name: string): PersonName | null {
  const trimmed = name.trim();
  if (trimmed === "") {
    return null;
  }
  if (looksLikeOrganization(trimmed)) {
    return { given: [], family: trimmed, organization: true };
  }
  return parsePersonName(trimmed);
}

/** "Family, Given", "Family, Jr., Given", or a double-braced organisation. */
export function bibtexName(
  person: PersonName,
  options: BibTextOptions
): string {
  const text = (value: string) =>
    bibText(value, { ...options, protectCapitals: false });
  if (person.organization) {
    return `{${text(person.family)}}`;
  }
  const parts = [text(person.family)];
  if (person.suffix) {
    parts.push(text(person.suffix));
  }
  if (person.given.length > 0) {
    parts.push(text(person.given.join(" ")));
  }
  return parts.join(", ");
}

export function joinNames(names: string[]): string {
  return names.join(" and ");
}

/* ------------------------------------------------------------------- keys */

const KEY_SPECIAL_LETTERS: Record<string, string> = {
  ß: "ss",
  æ: "ae",
  Æ: "AE",
  œ: "oe",
  Œ: "OE",
  ø: "o",
  Ø: "O",
  ł: "l",
  Ł: "L",
  đ: "d",
  Đ: "D",
  ð: "d",
  þ: "th",
  Þ: "Th",
};

const RE_KEY_SPECIAL = /[ßæÆœŒøØłŁđĐðþÞ]/g;
const RE_COMBINING_MARKS = /[\u0300-\u036f]/g;
const RE_NON_ALNUM = /[^a-z0-9]+/g;
const RE_WORD_SPLIT = /\s+/;

/** Words skipped when picking a title's first significant word. */
const STOP_WORDS = new Set([
  "a",
  "an",
  "the",
  "on",
  "of",
  "in",
  "at",
  "to",
  "for",
  "and",
  "or",
  "with",
  "from",
  "into",
  "about",
  "is",
  "are",
  "was",
  "were",
  "how",
  "why",
  "what",
  "when",
  "new",
]);

/** "Cuenca-Jiménez" → "cuencajimenez": ASCII letters and digits, lowercase. */
export function keyToken(raw: string): string {
  return raw
    .replace(RE_KEY_SPECIAL, (char) => KEY_SPECIAL_LETTERS[char] ?? char)
    .normalize("NFD")
    .replace(RE_COMBINING_MARKS, "")
    .toLowerCase()
    .replace(RE_NON_ALNUM, "");
}

export function firstSignificantWord(title: string): string {
  for (const raw of plainText(title).split(RE_WORD_SPLIT)) {
    const token = keyToken(raw);
    if (token.length >= 3 && !STOP_WORDS.has(token)) {
      return token;
    }
  }
  return "";
}

/**
 * surname + year + first significant title word. An organisation contributes its
 * first significant word; with no author at all, `fallback` (a site or publisher
 * name) stands in.
 */
export function citationKey({
  author,
  fallback = "",
  year = "",
  title = "",
}: {
  author?: PersonName | null;
  fallback?: string;
  year?: string;
  title?: string;
}): string {
  let lead = "";
  if (author?.organization) {
    lead = firstSignificantWord(author.family) || keyToken(author.family);
  } else if (author) {
    lead = keyToken(author.family);
  }
  if (lead === "") {
    lead = firstSignificantWord(fallback) || keyToken(fallback);
  }
  const key = `${lead}${keyToken(year)}${firstSignificantWord(title)}`;
  return key === "" ? "ref" : key;
}

const SUFFIX_LETTERS = "abcdefghijklmnopqrstuvwxyz";

/** smith2024 ×2 → smith2024a, smith2024b. Keys that are already unique stay as they are. */
export function uniqueKeys(keys: string[]): string[] {
  const counts = new Map<string, number>();
  for (const key of keys) {
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const seen = new Map<string, number>();
  return keys.map((key) => {
    if ((counts.get(key) ?? 0) < 2) {
      return key;
    }
    const index = seen.get(key) ?? 0;
    seen.set(key, index + 1);
    const suffix =
      index < SUFFIX_LETTERS.length ? SUFFIX_LETTERS[index] : String(index + 1);
    return `${key}${suffix}`;
  });
}

/* ----------------------------------------------------------------- entries */

const MONTH_MACROS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
] as const;

/** BibTeX's predefined month macro (`mar`), or "" for anything outside 1–12. */
export function monthMacro(month: number | undefined): string {
  if (!month || month < 1 || month > 12) {
    return "";
  }
  return MONTH_MACROS[month - 1];
}

/**
 * One entry, fields aligned:
 *
 *     @misc{santos2022robokrill,
 *       title         = {RoboKrill: a metachronal drag-based swimmer robot},
 *       ...
 *     }
 *
 * Fields with an empty value are dropped, so a builder can list every field it knows
 * about and let missing metadata fall away.
 */
export function formatBibEntry(
  type: string,
  key: string,
  fields: BibField[]
): string {
  const kept = fields.filter((field) => field.value.trim() !== "");
  const width = Math.max(0, ...kept.map((field) => field.name.length));
  const lines = kept.map((field) => {
    const value = field.bare ? field.value : `{${field.value}}`;
    return `  ${field.name.padEnd(width)} = ${value}`;
  });
  return `@${type}{${key},\n${lines.join(",\n")}\n}`;
}
